import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Seo from '../components/shared/Seo';
import AnimatedSection from '../components/ui/AnimatedSection';
import { useData } from '../context/DataContext';
import { useTranslation } from 'react-i18next';
import { Linkedin } from 'react-feather';
import PersonAvatar from '../components/ui/PersonAvatar';

const AttorneyProfilePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { authors, firmInfo } = useData();
  const { t } = useTranslation('common');

  const attorney = authors.find(a => a.id === slug);

  if (!attorney) {
    return <Navigate to="/about" replace />;
  }

  // Schema for Person
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": attorney.name,
    "jobTitle": attorney.title,
    ...(attorney.imageUrl ? { "image": attorney.imageUrl.startsWith('/') ? `${firmInfo.url}${attorney.imageUrl}` : attorney.imageUrl } : {}),
    "description": attorney.bio,
    "worksFor": {
      "@type": "LegalService",
      "name": firmInfo.name,
      "url": firmInfo.url
    },
    "url": `${firmInfo.url}/attorneys/${attorney.id}`,
    ...(attorney.barNumber ? {
      "hasCredential": {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Bar Admission",
        "identifier": attorney.barNumber,
        "recognizedBy": {
          "@type": "Organization",
          "name": "Utah State Bar"
        }
      }
    } : {})
  };

  return (
    <>
      <Seo
        title={`${attorney.name} - ${attorney.title} | ${firmInfo.name}`}
        description={attorney.bio.substring(0, 160)}
        schema={personSchema}
      />
      
      <div className="pt-32 pb-24">
        <AnimatedSection className="mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="md:col-span-1">
              <PersonAvatar name={attorney.name} imageUrl={attorney.imageUrl} className="w-full h-auto rounded-xl shadow-lg aspect-[3/4]" initialsClassName="text-7xl" />
              <div className="mt-6 flex flex-col gap-4">
                <a href={`tel:${firmInfo.telephone}`} className="text-center bg-brand-gold text-black font-bold py-3 px-6 rounded-lg hover:bg-brand-gold-light transition-colors">
                  Contact {attorney.name.split(' ')[0]}
                </a>
                {attorney.id === 'christian-burridge' && (
                  <a href="https://www.linkedin.com/in/christian-burridge-esq/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 text-white/80 hover:text-white border border-white/20 py-2 rounded-lg transition-colors">
                    <Linkedin size={18} />
                    <span>LinkedIn Profile</span>
                  </a>
                )}
              </div>
            </div>
            
            <div className="md:col-span-2">
              <h1 className="text-4xl md:text-5xl font-serif font-bold mb-2">{attorney.name}</h1>
              <p className="text-xl text-brand-gold-light mb-8">{attorney.title}</p>
              
              <div className="prose prose-invert max-w-none prose-lg">
                <p>{attorney.bio}</p>
                
                {attorney.id === 'christian-burridge' && (
                  <>
                    <h3>Education</h3>
                    <ul>
                      <li>J.D., University of Utah S.J. Quinney College of Law</li>
                      <li>B.A., Brigham Young University</li>
                    </ul>
                    
                    <h3>Bar Admissions</h3>
                    <ul>
                      <li>Utah State Bar</li>
                      <li>U.S. District Court District of Utah</li>
                    </ul>
                    
                    <h3>Professional Associations</h3>
                    <ul>
                      <li>Utah Association for Justice</li>
                      <li>American Association for Justice</li>
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </>
  );
};

export default AttorneyProfilePage;
